import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { toBuddhistYear } from 'utils/app.utils'
import { forceTextOnDateCells } from 'utils/excel.utils'

export interface RemunerationReportData {
    branchName: string
    date: string
    t: any
    cash: boolean
    df: boolean
    treatmentRight: boolean
    rowDailyBalance: Array<{
        sumTreatment: number
        sumProduct: number
        sumAll: number | null
    }>
    rowIncome: Array<{
        detail: string
        price: number
        total: number
    }>
    rowDf: Array<{
        doctorId: number | null
        detail: string | null
        price: number
        total: number
    }>
    rowTreatmentRight: Array<{
        detail: string
        price: number
        total: number
    }>
}

interface PaymentItem {
    description: any
    amount: number
    fullAmount?: number | string
    isBold?: boolean
}

export async function ExportToExcel(report: RemunerationReportData) {
    const items: PaymentItem[] = []

    const bal = report.rowDailyBalance[0] || { sumProduct: 0, sumTreatment: 0, sumAll: 0 }
    items.push({ description: report.t('REPORT.TABLE.REMITTANCE.NET_TOOLS'), amount: bal.sumProduct })
    items.push({ description: report.t('REPORT.TABLE.REMITTANCE.NET_OPERATIVE'), amount: bal.sumTreatment })
    items.push({ description: report.t('REPORT.TABLE.REMITTANCE.NET_DAILY'), amount: bal.sumAll ?? 0, isBold: false })

    report.rowIncome.forEach(it => {
        items.push({
            description: it.detail,
            amount: it.price ?? 0,
            fullAmount: it.total ?? 0,
            isBold: false
        })
    })
    const sumIncome = report.rowIncome.reduce((s, x) => s + x.price, 0)
    items.push({
        description: report.t('REPORT.TABLE.REMITTANCE.TOTAL_INCOME'),
        amount: sumIncome,
        fullAmount: '-',
        isBold: false
    })

    report.rowTreatmentRight.forEach(it => {
        items.push({
            description: it.detail,
            amount: it.price,
            fullAmount: it.total,
            isBold: false
        })
    })
    const sumTR = report.rowTreatmentRight.reduce((s, x) => s + x.price, 0)
    items.push({
        description: report.t('REPORT.TABLE.REMITTANCE.TOTAL_INCOME'),
        amount: sumTR,
        fullAmount: '-',
        isBold: false
    })

    // 4) DF
    report.rowDf.filter(it => it.detail).forEach(it => {
        items.push({
            description: it.detail,
            amount: it.price,
            fullAmount: it.total,
            isBold: false
        })
    })
    const sumDF = report.rowDf.reduce((s, x) => s + x.price, 0)
    items.push({
        description: report.t('REPORT.TABLE.REMITTANCE.TOTAL_EXPENSES'),
        amount: sumDF,
        fullAmount: '-',
        isBold: false
    })
    items.push({
        description: report.t('REPORT.TABLE.REMITTANCE.TOTAL_INCOME_EXPENSES'),
        amount: -sumDF,
        fullAmount: '-',
        isBold: false
    })

    const wb = new ExcelJS.Workbook()
    const ws = wb.addWorksheet('ใบส่งเงิน (แบบที่1)', { pageSetup: { orientation: 'portrait' } })
    ws.columns = []
    ws.columns = [{ width: 40 }, { width: 12 }, { width: 12 }]

    let r = 1
    const parts: string[] = []
    if (report.df) parts.push(report.t('REPORT.TABLE.INCOME_MONTH.DF'))
    if (report.cash) parts.push(report.t('REPORT.TABLE.INCOME_MONTH.CASH'))
    if (report.treatmentRight) parts.push(report.t('REPORT.TABLE.INCOME_MONTH.TREATMENT_RIGHT'))
    const headerTitle = parts.length ? `(${parts.join(', ')})` : ''

    ws.mergeCells(r, 1, r, 3)
    ws.getCell(r, 1).value = `${report.t('REPORT.REPORT_NAME.REMITTANCE')} ${headerTitle}`
    ws.getCell(r, 1).font = { size: 14, bold: true, color: { argb: 'FF000000' } }
    ws.getCell(r, 1).alignment = { horizontal: 'center' }
    r++

    ws.mergeCells(r, 1, r, 3)
    ws.getCell(r, 1).value =
        `${report.t('REPORT.TITLE.BRANCH')} : ${report.branchName}` +
        `    ${report.t('REPORT.TITLE.DATE')} : ${toBuddhistYear(moment(report.date), 'DD/MM/YYYY')}`
    ws.getCell(r, 1).font = { size: 12, color: { argb: 'FF000000' } }
    ws.getCell(r, 1).alignment = { horizontal: 'left' }
    r += 2

    const headerRow = ws.addRow([
        report.t('REPORT.TABLE.REMITTANCE.LIST'),
        report.t('REPORT.TABLE.REMITTANCE.QTY'),
        report.t('REPORT.TABLE.REMITTANCE.ALL_QTY')
    ])
    headerRow.eachCell(cell => {
        cell.font = { bold: true, size: 12, color: { argb: 'FF000000' } }
        cell.alignment = { horizontal: 'center' }
        cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        }
    })
    r++

    items.forEach(it => {
        const row = ws.addRow([
            it.description,
            it.amount,
            it.fullAmount != null ? it.fullAmount : '-'
        ])
        row.getCell(2).numFmt = '#,##0.00'
        if (typeof it.fullAmount === 'number') {
            row.getCell(3).numFmt = '#,##0.00'
        }
        [1, 2, 3].forEach(ci => {
            const cell = row.getCell(ci)
            cell.alignment = { horizontal: ci === 1 ? 'left' : 'right' }
            cell.font = { bold: !!it.isBold, color: { argb: 'FF000000' } }
            cell.border = { bottom: { style: 'thin' } }
        })
    })

    forceTextOnDateCells(wb)
    const buf = await wb.xlsx.writeBuffer()
    saveAs(
        new Blob([buf]),
        `${report.t('REPORT.REPORT_NAME.REMITTANCE')}_${moment().format('YYYYMMDD')}.xlsx`
    )
}

